import { wrapper, createApiClient, Result, ApiError } from "../base";
import DocumentDO = App.Data.Explorer.DocumentDO;
import { LaravelPaginatedCollection } from "@/types/collections";
import { FileSearchParams } from "@/types/fileSearch";
import StoredDocumentDO = App.Data.StoredDocumentDO;
import FileDTO = App.Data.FileDTO;
import ResourceTreeNodeDO = App.Data.ResourceTreeNodeDO;
import SharedResourcePermissionDTO = App.Data.SharedResourcePermissionDTO;
import SharedResourcePermissionForRoleDTO = App.Data.SharedResourcePermissionForRoleDTO;
import PublicUserDO = App.Data.PublicUserDO;
import RoleDO = App.Data.RoleDO;
import AbilityName = App.Enums.AbilityName;

const baseClient = createApiClient();

type GetAllStoredDocumentsForUserParams = {
	"include[document.dataTables]"?: boolean;
};

export default class {
	show(documentUuid: string): Promise<Result<DocumentDO, ApiError>> {
		return wrapper(baseClient.get(`documents/${documentUuid}`));
	}
	getAllStoredDocumentsForUser(
		params: GetAllStoredDocumentsForUserParams = {}
	): Promise<Result<StoredDocumentDO[], ApiError>> {
		return wrapper(
			baseClient.get(`documents/all-stored-documents-for-user`, {
				params,
			})
		);
	}
	getRecentStoredDocumentsForUser(
		limit = 5
	): Promise<Result<StoredDocumentDO[], ApiError>> {
		return wrapper(
			baseClient.get(`documents/recent-stored-documents-for-user`, {
				params: { limit },
			})
		);
	}

	openStoredDocument(
		storedDocument: FileDTO,
		parentDocument?: DocumentDO
	): Promise<Result<App.Data.Explorer.OpenDocumentDTO, ApiError>> {
		return wrapper(
			baseClient.get(
				`documents/open/${storedDocument.uuid}/${parentDocument?.uuid ?? null}`
			)
		);
	}

	getUsersWithPermissionsToStoredDocument(
		storedDocument: FileDTO
	): Promise<Result<SharedResourcePermissionDTO[], ApiError>> {
		return wrapper(
			baseClient.get(
				`documents/get-users-with-permissions-to/${storedDocument.uuid}`
			)
		);
	}

	revokePermissionsToStoredDocumentForUser(
		storedDocument: FileDTO,
		user: PublicUserDO
	): Promise<Result<App.Data.StoredDocumentDO, ApiError>> {
		return wrapper(
			baseClient.patch(
				`documents/revoke-permissions-to-stored-document-for-user/${user.id}`,
				storedDocument
			)
		);
	}

	grantPermissionsToStoredDocumentForUser(
		storedDocument: FileDTO,
		user: PublicUserDO,
		ability: AbilityName
	): Promise<Result<App.Data.StoredDocumentDO, ApiError>> {
		return wrapper(
			baseClient.patch(
				`documents/grant-permissions-to-stored-document-for-user/${user.id}/${ability}`,
				storedDocument
			)
		);
	}

	getRolesWithPermissionsToStoredDocument(
		storedDocument: FileDTO
	): Promise<Result<SharedResourcePermissionForRoleDTO[], ApiError>> {
		return wrapper(
			baseClient.get(
				`documents/get-roles-with-permissions-to/${storedDocument.uuid}`
			)
		);
	}

	revokePermissionsToStoredDocumentForRole(
		storedDocument: FileDTO,
		role: RoleDO
	): Promise<Result<App.Data.StoredDocumentDO, ApiError>> {
		return wrapper(
			baseClient.patch(
				`documents/revoke-permissions-to-stored-document-for-role/${role.id}`,
				storedDocument
			)
		);
	}

	grantPermissionsToStoredDocumentForRole(
		storedDocument: FileDTO,
		role: RoleDO,
		ability: AbilityName
	): Promise<Result<App.Data.StoredDocumentDO, ApiError>> {
		return wrapper(
			baseClient.patch(
				`documents/grant-permissions-to-stored-document-for-role/${role.id}/${ability}`,
				storedDocument
			)
		);
	}

	createStoredDocument(
		storedDocument: App.Data.StoredDocumentDO,
		documentUUID: string
	): Promise<Result<App.Data.StoredDocumentDO, ApiError>> {
		return wrapper(
			baseClient.post(`documents/${documentUUID}`, storedDocument)
		);
	}

	updateStoredDocumentDocument(
		storedDocument: App.Data.StoredDocumentDO,
		documentUUID: string
	): Promise<Result<App.Data.StoredDocumentDO, ApiError>> {
		return wrapper(
			baseClient.put(
				`documents/assign-document/${documentUUID}`,
				storedDocument
			)
		);
	}
	updateStoredDocument(
		storedDocument: App.Data.StoredDocumentDO
	): Promise<Result<App.Data.StoredDocumentDO, ApiError>> {
		return wrapper(
			baseClient.put(`documents/${storedDocument.uuid}`, storedDocument)
		);
	}

	toggleOrSetFavoriteStoredDocument(
		storedDocument: FileDTO
	): Promise<Result<App.Data.StoredDocumentDO, ApiError>> {
		return wrapper(
			baseClient.patch(`documents/toggle-or-set-favorite`, storedDocument)
		);
	}

	removeStoredDocument(storedDocument: FileDTO): Promise<Result<[], ApiError>> {
		return wrapper(baseClient.delete(`documents/${storedDocument.uuid}`));
	}

	batchRemoveStoredDocuments(
		storedDocuments: FileDTO[]
	): Promise<Result<[], ApiError>> {
		return wrapper(
			baseClient.delete(`/documents`, {
				data: {
					uuids: storedDocuments.map((storedDocument) => storedDocument.uuid),
				},
			})
		);
	}

	/** Filtered, sorted, paginated search across the user's own stored documents. */
	searchStoredDocuments(
		params: FileSearchParams = {}
	): Promise<Result<LaravelPaginatedCollection<FileDTO>, ApiError>> {
		return wrapper(baseClient.get(`documents/search`, { params }));
	}

	getOwnedStoredDocumentsByResourceTreeNode(
		resourceTreeNode: ResourceTreeNodeDO
	): Promise<Result<App.Data.FileDTO[], ApiError>> {
		return wrapper(
			baseClient.get(
				`documents/get-owned-stored-documents-by-resource-tree-node/${resourceTreeNode.uuid}`
			)
		);
	}

	getStoredDocumentsSharedWithMe(): Promise<
		Result<App.Data.FileDTO[], ApiError>
	> {
		return wrapper(
			baseClient.get(`documents/get-stored-documents-shared-with-me`)
		);
	}
}
